/**
 * @file useBuildingOperations.js
 * @description Building, Level, and Space UI operations composable
 *
 * Extracted from UnifiedBrickSchemaManager.vue
 * Handles:
 * - Building/Level/Space form dialog management
 * - Building/Level/Space CRUD operations with UI feedback
 * - Building hierarchy navigation
 */

import { ref } from 'vue';
import { useQuasar } from 'quasar';

/**
 * Composable for building hierarchy UI operations
 * @returns {Object} Building operations state and methods
 */
export function useBuildingOperations() {
  const $q = useQuasar();

  // Dialog visibility state
  const showBuildingDialog = ref(false);
  const showLevelDialog = ref(false);
  const showSpaceDialog = ref(false);
  const showEditBuildingDialog = ref(false);
  const showEditLevelDialog = ref(false);
  const showEditSpaceDialog = ref(false);

  // Form data for creating new entities
  const newBuilding = ref({
    name: '',
    description: '',
    brick_class: 'brick:Building',
  });

  const newLevel = ref({
    name: '',
    level_number: '',
    description: '',
    brick_class: 'brick:Floor',
  });

  const newSpace = ref({
    name: '',
    description: '',
    brick_class: 'brick:Space',
    area: null,
    metadata: {
      area_unit_id: null,
    },
  });

  // Editing state
  const editingBuilding = ref(null);
  const editingLevel = ref(null);
  const editingSpace = ref(null);

  // Selected parent context
  const selectedBuilding = ref(null);
  const selectedLevel = ref(null);

  // =============================================
  // BUILDING OPERATIONS
  // =============================================

  /**
   * Reset new building form
   */
  const resetNewBuildingForm = () => {
    newBuilding.value = {
      name: '',
      description: '',
      brick_class: 'brick:Building',
    };
  };

  /**
   * Open add building dialog
   */
  const openAddBuildingDialog = () => {
    resetNewBuildingForm();
    showBuildingDialog.value = true;
  };

  /**
   * Open edit building dialog
   * @param {Object} building - Building data to edit
   */
  const openEditBuildingDialog = (building) => {
    if (!building) {
      $q.notify({
        type: 'negative',
        message: 'No building selected for editing',
      });
      return;
    }

    editingBuilding.value = {
      id: building.id,
      name: building.name || '',
      description: building.description || '',
      brick_class: building.brick_class || 'brick:Building',
    };
    showEditBuildingDialog.value = true;
  };

  /**
   * Submit building creation
   * @param {Object} dependencies - Required dependencies
   * @returns {Promise<Object|null>} Created building or null
   */
  const submitCreateBuilding = async (dependencies) => {
    const {
      buildingApi,
      refreshData,
    } = dependencies;

    // Validation
    if (!newBuilding.value.name) {
      $q.notify({
        type: 'negative',
        message: 'Building name is required',
      });
      return null;
    }

    try {
      const buildingData = {
        name: newBuilding.value.name,
        description: newBuilding.value.description || null,
        brick_class: newBuilding.value.brick_class || 'brick:Building',
      };

      const created = await buildingApi.createBuilding(buildingData);

      // Reset form and close dialog
      resetNewBuildingForm();
      showBuildingDialog.value = false;

      // Refresh data
      if (refreshData) {
        await refreshData();
      }

      return created;
    } catch (error) {
      // Error notification handled by API composable
      return null;
    }
  };

  /**
   * Submit building update
   * @param {Object} dependencies - Required dependencies
   * @returns {Promise<Object|null>} Updated building or null
   */
  const submitUpdateBuilding = async (dependencies) => {
    const {
      buildingApi,
      refreshData,
    } = dependencies;

    if (!editingBuilding.value?.id) {
      $q.notify({
        type: 'negative',
        message: 'Building ID is required for update',
      });
      return null;
    }

    if (!editingBuilding.value.name) {
      $q.notify({
        type: 'negative',
        message: 'Building name is required',
      });
      return null;
    }

    try {
      const buildingData = {
        name: editingBuilding.value.name,
        description: editingBuilding.value.description || null,
        brick_class: editingBuilding.value.brick_class || 'brick:Building',
      };

      const updated = await buildingApi.updateBuilding(
        editingBuilding.value.id,
        buildingData,
      );

      // Close dialog
      showEditBuildingDialog.value = false;
      editingBuilding.value = null;

      // Refresh data
      if (refreshData) {
        await refreshData();
      }

      return updated;
    } catch (error) {
      // Error notification handled by API composable
      return null;
    }
  };

  // =============================================
  // LEVEL OPERATIONS
  // =============================================

  /**
   * Reset new level form
   */
  const resetNewLevelForm = () => {
    newLevel.value = {
      name: '',
      level_number: '',
      description: '',
      brick_class: 'brick:Floor',
    };
  };

  /**
   * Open add level dialog for a building
   * @param {Object} building - Parent building data
   */
  const openAddLevelDialog = (building) => {
    if (!building) {
      $q.notify({
        type: 'negative',
        message: 'Please select a building first',
      });
      return;
    }

    selectedBuilding.value = building;
    resetNewLevelForm();
    showLevelDialog.value = true;
  };

  /**
   * Open add sibling level dialog (same building as existing level)
   * @param {Object} levelNode - Existing level node
   * @param {Array} treeNodes - Tree nodes to search for parent building
   */
  const openAddSiblingLevelDialog = (levelNode, treeNodes) => {
    // Find the parent building
    const findParentBuilding = (nodes, levelId) => {
      for (const node of nodes) {
        if (node.type === 'building' && node.children) {
          for (const child of node.children) {
            if (child.id === levelId) {
              return node.data;
            }
          }
        }
        if (node.children) {
          const found = findParentBuilding(node.children, levelId);
          if (found) {return found;}
        }
      }
      return null;
    };

    const parentBuilding = findParentBuilding(treeNodes || [], levelNode.id);
    if (parentBuilding) {
      selectedBuilding.value = parentBuilding;
      resetNewLevelForm();
      showLevelDialog.value = true;
    } else {
      $q.notify({
        type: 'negative',
        message: 'Could not find parent building for this level',
      });
    }
  };

  /**
   * Open edit level dialog
   * @param {Object} level - Level data to edit
   */
  const openEditLevelDialog = (level) => {
    if (!level) {
      $q.notify({
        type: 'negative',
        message: 'No level selected for editing',
      });
      return;
    }

    editingLevel.value = {
      id: level.id,
      name: level.name || '',
      level_number: level.level_number || '',
      description: level.description || '',
      brick_class: level.brick_class || 'brick:Floor',
    };
    showEditLevelDialog.value = true;
  };

  /**
   * Submit level creation
   * @param {Object} dependencies - Required dependencies
   * @returns {Promise<Object|null>} Created level or null
   */
  const submitCreateLevel = async (dependencies) => {
    const {
      buildingApi,
      refreshData,
    } = dependencies;

    // Validation
    if (!newLevel.value.name) {
      $q.notify({
        type: 'negative',
        message: 'Level name is required',
      });
      return null;
    }

    if (!selectedBuilding.value?.id) {
      $q.notify({
        type: 'negative',
        message: 'Please select a building first',
      });
      return null;
    }

    try {
      const levelData = {
        name: newLevel.value.name,
        level_number: newLevel.value.level_number || null,
        description: newLevel.value.description || null,
        brick_class: newLevel.value.brick_class || 'brick:Floor',
        building_id: selectedBuilding.value.id,
      };

      const created = await buildingApi.createLevel(levelData);

      // Reset form and close dialog
      resetNewLevelForm();
      showLevelDialog.value = false;

      // Refresh data
      if (refreshData) {
        await refreshData();
      }

      return created;
    } catch (error) {
      // Error notification handled by API composable
      return null;
    }
  };

  /**
   * Submit level update
   * @param {Object} dependencies - Required dependencies
   * @returns {Promise<Object|null>} Updated level or null
   */
  const submitUpdateLevel = async (dependencies) => {
    const {
      buildingApi,
      refreshData,
    } = dependencies;

    if (!editingLevel.value?.id) {
      $q.notify({
        type: 'negative',
        message: 'Level ID is required for update',
      });
      return null;
    }

    if (!editingLevel.value.name) {
      $q.notify({
        type: 'negative',
        message: 'Level name is required',
      });
      return null;
    }

    try {
      const levelData = {
        name: editingLevel.value.name,
        level_number: editingLevel.value.level_number || null,
        description: editingLevel.value.description || null,
        brick_class: editingLevel.value.brick_class || 'brick:Floor',
      };

      const updated = await buildingApi.updateLevel(
        editingLevel.value.id,
        levelData,
      );

      // Close dialog
      showEditLevelDialog.value = false;
      editingLevel.value = null;

      // Refresh data
      if (refreshData) {
        await refreshData();
      }

      return updated;
    } catch (error) {
      // Error notification handled by API composable
      return null;
    }
  };

  // =============================================
  // SPACE OPERATIONS
  // =============================================

  /**
   * Reset new space form
   */
  const resetNewSpaceForm = () => {
    newSpace.value = {
      name: '',
      description: '',
      brick_class: 'brick:Space',
      area: null,
      metadata: {
        area_unit_id: null,
      },
    };
  };

  /**
   * Open add space dialog for a level
   * @param {Object} level - Parent level data
   */
  const openAddSpaceDialog = (level) => {
    if (!level) {
      $q.notify({
        type: 'negative',
        message: 'Please select a level first',
      });
      return;
    }

    selectedLevel.value = level;
    resetNewSpaceForm();
    showSpaceDialog.value = true;
  };

  /**
   * Open edit space dialog
   * @param {Object} space - Space data to edit
   */
  const openEditSpaceDialog = (space) => {
    if (!space) {
      $q.notify({
        type: 'negative',
        message: 'No space selected for editing',
      });
      return;
    }

    editingSpace.value = {
      id: space.id,
      name: space.name || '',
      description: space.description || '',
      brick_class: space.brick_class || 'brick:Space',
      area: space.area || null,
      metadata: space.metadata || {},
    };
    showEditSpaceDialog.value = true;
  };

  /**
   * Submit space creation
   * @param {Object} dependencies - Required dependencies
   * @returns {Promise<Object|null>} Created space or null
   */
  const submitCreateSpace = async (dependencies) => {
    const {
      buildingApi,
      refreshData,
    } = dependencies;

    // Validation
    if (!newSpace.value.name) {
      $q.notify({
        type: 'negative',
        message: 'Space name is required',
      });
      return null;
    }

    if (!selectedLevel.value?.id) {
      $q.notify({
        type: 'negative',
        message: 'Please select a level first',
      });
      return null;
    }

    try {
      const spaceData = {
        name: newSpace.value.name,
        description: newSpace.value.description || null,
        brick_class: newSpace.value.brick_class || 'brick:Space',
        area: newSpace.value.area || null,
        metadata: newSpace.value.metadata || {},
        level_id: selectedLevel.value.id,
      };

      const created = await buildingApi.createSpace(spaceData);

      // Reset form and close dialog
      resetNewSpaceForm();
      showSpaceDialog.value = false;

      // Refresh data
      if (refreshData) {
        await refreshData();
      }

      return created;
    } catch (error) {
      // Error notification handled by API composable
      return null;
    }
  };

  /**
   * Submit space update
   * @param {Object} dependencies - Required dependencies
   * @returns {Promise<Object|null>} Updated space or null
   */
  const submitUpdateSpace = async (dependencies) => {
    const {
      buildingApi,
      refreshData,
    } = dependencies;

    if (!editingSpace.value?.id) {
      $q.notify({
        type: 'negative',
        message: 'Space ID is required for update',
      });
      return null;
    }

    if (!editingSpace.value.name) {
      $q.notify({
        type: 'negative',
        message: 'Space name is required',
      });
      return null;
    }

    try {
      const spaceData = {
        name: editingSpace.value.name,
        description: editingSpace.value.description || null,
        brick_class: editingSpace.value.brick_class || 'brick:Space',
        area: editingSpace.value.area || null,
        metadata: editingSpace.value.metadata || {},
      };

      const updated = await buildingApi.updateSpace(
        editingSpace.value.id,
        spaceData,
      );

      // Close dialog
      showEditSpaceDialog.value = false;
      editingSpace.value = null;

      // Refresh data
      if (refreshData) {
        await refreshData();
      }

      return updated;
    } catch (error) {
      // Error notification handled by API composable
      return null;
    }
  };

  // =============================================
  // DELETE OPERATIONS WITH CONFIRMATION
  // =============================================

  /**
   * Delete building with confirmation
   * @param {Object} building - Building to delete
   * @param {Object} dependencies - Required dependencies
   */
  const confirmDeleteBuilding = async (building, dependencies) => {
    const {
      buildingApi,
      refreshData,
      showCascadeDeleteDialog,
    } = dependencies;

    if (!building?.id) {
      $q.notify({
        type: 'negative',
        message: 'Building ID is required for deletion',
      });
      return;
    }

    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete building "${building.name}"? This may affect levels, spaces, and equipment within.`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
      },
    }).onOk(async () => {
      try {
        await buildingApi.deleteBuilding(building.id, false);

        $q.notify({
          type: 'positive',
          message: `Building "${building.name}" deleted successfully`,
        });

        if (refreshData) {
          await refreshData();
        }
      } catch (error) {
        // Check if this is a cascade-able error
        if (error.response?.status === 422 && error.response?.data?.can_cascade) {
          if (showCascadeDeleteDialog) {
            showCascadeDeleteDialog({ data: building, label: building.name, type: 'building' }, error.response.data);
          }
        }
      }
    });
  };

  /**
   * Delete level with confirmation
   * @param {Object} level - Level to delete
   * @param {Object} dependencies - Required dependencies
   */
  const confirmDeleteLevel = async (level, dependencies) => {
    const {
      buildingApi,
      refreshData,
      showCascadeDeleteDialog,
    } = dependencies;

    if (!level?.id) {
      $q.notify({
        type: 'negative',
        message: 'Level ID is required for deletion',
      });
      return;
    }

    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete level "${level.name}"? This may affect spaces and equipment within.`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
      },
    }).onOk(async () => {
      try {
        await buildingApi.deleteLevel(level.id, false);

        $q.notify({
          type: 'positive',
          message: `Level "${level.name}" deleted successfully`,
        });

        if (refreshData) {
          await refreshData();
        }
      } catch (error) {
        // Check if this is a cascade-able error
        if (error.response?.status === 422 && error.response?.data?.can_cascade) {
          if (showCascadeDeleteDialog) {
            showCascadeDeleteDialog({ data: level, label: level.name, type: 'level' }, error.response.data);
          }
        }
      }
    });
  };

  /**
   * Delete space with confirmation
   * @param {Object} space - Space to delete
   * @param {Object} dependencies - Required dependencies
   */
  const confirmDeleteSpace = async (space, dependencies) => {
    const {
      buildingApi,
      refreshData,
      showCascadeDeleteDialog,
    } = dependencies;

    if (!space?.id) {
      $q.notify({
        type: 'negative',
        message: 'Space ID is required for deletion',
      });
      return;
    }

    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete space "${space.name}"? This may affect equipment within.`,
      cancel: true,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
      },
    }).onOk(async () => {
      try {
        await buildingApi.deleteSpace(space.id, false);

        $q.notify({
          type: 'positive',
          message: `Space "${space.name}" deleted successfully`,
        });

        if (refreshData) {
          await refreshData();
        }
      } catch (error) {
        // Check if this is a cascade-able error
        if (error.response?.status === 422 && error.response?.data?.can_cascade) {
          if (showCascadeDeleteDialog) {
            showCascadeDeleteDialog({ data: space, label: space.name, type: 'space' }, error.response.data);
          }
        }
      }
    });
  };

  return {
    // Dialog visibility
    showBuildingDialog,
    showLevelDialog,
    showSpaceDialog,
    showEditBuildingDialog,
    showEditLevelDialog,
    showEditSpaceDialog,

    // Form data
    newBuilding,
    newLevel,
    newSpace,
    editingBuilding,
    editingLevel,
    editingSpace,

    // Selected context
    selectedBuilding,
    selectedLevel,

    // Building methods
    resetNewBuildingForm,
    openAddBuildingDialog,
    openEditBuildingDialog,
    submitCreateBuilding,
    submitUpdateBuilding,
    confirmDeleteBuilding,

    // Level methods
    resetNewLevelForm,
    openAddLevelDialog,
    openAddSiblingLevelDialog,
    openEditLevelDialog,
    submitCreateLevel,
    submitUpdateLevel,
    confirmDeleteLevel,

    // Space methods
    resetNewSpaceForm,
    openAddSpaceDialog,
    openEditSpaceDialog,
    submitCreateSpace,
    submitUpdateSpace,
    confirmDeleteSpace,
  };
}
